/**
 * Effect-native chunk fetcher: the engine's single authority for TTS chunk
 * fetching. Orchestration is Effect end to end — an `Effect.Semaphore` for the
 * concurrency cap, a `Deferred` per chunk for single-flight, fibers in a
 * fetcher-owned `Scope` for the work (§5 of
 * docs/projects/active/pausing-audio-chunk-playing-refactor/playback-architecture-decisions.md).
 *
 * Policy per request (see
 * docs/projects/active/pausing-audio-chunk-playing-refactor/playback-stall-prevention-plan.md):
 * - single-flight per chunk; bounded concurrency (the hard cap on what one
 *   client puts on the TTS pool) — over-cap prefetches park on the semaphore
 *   instead of being dropped, so a cold document can fill its runway (§4);
 * - failures are CLASSIFIED before they drive anything: permanent (404/400 —
 *   never retried), session (401 — trips the circuit immediately), transient
 *   (everything else — exponential backoff with jitter, `retry_after_seconds`
 *   honored as a cooldown floor for prefetch and current fetches alike);
 * - a deadline per network attempt (`requestTimeoutMs`): an attempt that
 *   outlives it fails as transient instead of holding the loading state
 *   indefinitely — a cold voice change makes every chunk a fresh synthesis,
 *   so a hung request here is otherwise an unbounded spinner;
 * - a circuit breaker across distinct chunks: the first current-priority fetch
 *   after an open probes immediately, later probes pace on `probeIntervalMs`,
 *   and a per-chunk cooldown floor always outranks both;
 * - a per-chunk retry budget the engine consults instead of a fixed
 *   consecutive-failure count.
 *
 * Admission and single-flight bookkeeping is synchronous (`plan`) because the
 * engine issues fetches back to back off one JS turn; deferring it into the
 * runtime would let two calls for one chunk both miss the in-flight map. The
 * whole admission-to-fork step runs under `uninterruptibleMask` (awaits are
 * restored): a caller interrupted between registering a pending entry and
 * forking its work fiber would otherwise strand an entry no fiber ever
 * settles, hanging every later fetch of that chunk.
 *
 * Policy time comes from the injected `nowMs`, not `Clock`: cooldowns are
 * checked, never awaited (parking is on the semaphore) and the engine reads
 * `retryBudgetExhausted`/`failureCount`/`circuitState` synchronously. The one
 * `Clock` use is the per-attempt deadline inside `attempt`.
 *
 * TanStack holds no chunk data at all: each request's `readCached`/
 * `writeCached` point at the engine's chunk-cache atom (see chunk-cache.ts),
 * which `useCurrentAudioChunkQuery` reads to render the current chunk.
 */
import {
  apiRetryAfterMs,
  apiRetryClass,
  ApiRetryClass,
} from '@listening/api-contract/errors';
import {
  Cause,
  Deferred,
  Effect,
  Exit,
  type Fiber,
  Option,
  Schema,
  Scope,
  Semaphore,
} from 'effect';

export type ChunkFetchPriority = 'current' | 'prefetch';

export type ChunkFetchSkipReason =
  | 'circuit-open'
  | 'cooldown'
  | 'gave-up'
  | 'superseded';

/** The fetcher declined to run this request. Never evidence against the chunk:
 * it is bookkeeping (superseded) or scheduling (cooldown, circuit, gave-up). */
export class ChunkFetchSkippedError extends Schema.TaggedError<ChunkFetchSkippedError>()(
  'ChunkFetchSkippedError',
  {
    reason: Schema.Literals([
      'circuit-open',
      'cooldown',
      'gave-up',
      'superseded',
    ]),
    key: Schema.String,
    message: Schema.String,
    /** For 'cooldown': how long until the key is eligible again. */
    retryInMs: Schema.optional(Schema.Finite),
  },
) {}

export const chunkFetchSkipped = (
  reason: ChunkFetchSkipReason,
  key: string,
  retryInMs?: number,
) =>
  ChunkFetchSkippedError.make({
    reason,
    key,
    message: `chunk fetch skipped (${reason}): ${key}`,
    ...(retryInMs === undefined ? {} : { retryInMs }),
  });

export const isChunkFetchSkippedError = (
  error: unknown,
): error is ChunkFetchSkippedError => Schema.is(ChunkFetchSkippedError)(error);

/** A network attempt landed and failed, already classified by policy class.
 * `failure` is the contract error the attempt actually produced. */
export class ChunkFetchFailedError extends Schema.TaggedError<ChunkFetchFailedError>()(
  'ChunkFetchFailedError',
  {
    classification: ApiRetryClass,
    key: Schema.String,
    message: Schema.String,
    failure: Schema.Unknown,
    retryAfterMs: Schema.optional(Schema.Finite),
  },
) {}

export const chunkFetchFailed = (
  classification: ApiRetryClass,
  key: string,
  failure: unknown,
  retryAfterMs?: number,
) =>
  ChunkFetchFailedError.make({
    classification,
    key,
    message: `chunk fetch failed (${classification}): ${key}`,
    failure,
    ...(retryAfterMs === undefined ? {} : { retryAfterMs }),
  });

export type ChunkFetchError = ChunkFetchSkippedError | ChunkFetchFailedError;

export type ChunkFetchCircuitState = 'closed' | 'open';

export type ChunkFetcherConfig = {
  /** Concurrent fetch cap per player — the hard bound on backend load. */
  maxConcurrent: number;
  baseCooldownMs: number;
  maxCooldownMs: number;
  /** Transient failures after which a chunk stops being prefetched. */
  maxAttemptsPerKey: number;
  /** Distinct failing chunks within `circuitWindowMs` that open the circuit. */
  circuitFailureThreshold: number;
  circuitWindowMs: number;
  /** Minimum spacing between probe fetches after the first one while open. */
  probeIntervalMs: number;
  /** How long the engine keeps retrying the CURRENT chunk before a terminal
   * fault — time-based, so slow failures can't stretch it and fast failures
   * can't exhaust it in seconds. */
  retryBudgetMs: number;
  /** Deadline on a single network attempt. Must clear a cold per-voice TTS
   * synthesis (the normal case right after a voice change); a timed-out
   * attempt classifies transient and re-enters the backoff/budget machinery. */
  requestTimeoutMs: number;
};

export const defaultChunkFetcherConfig: ChunkFetcherConfig = {
  maxConcurrent: 3,
  baseCooldownMs: 2_000,
  maxCooldownMs: 60_000,
  maxAttemptsPerKey: 5,
  circuitFailureThreshold: 4,
  circuitWindowMs: 60_000,
  probeIntervalMs: 30_000,
  retryBudgetMs: 30_000,
  requestTimeoutMs: 60_000,
};

/** Structural slice of `ManagedRuntime` the fetcher needs to run its ambient
 * log records. Generic in R so the engine passes its AppRuntime and tests pass
 * an empty runtime. Transitional: once the fetcher becomes a Layer-provided
 * service (atom off-ramp workstream B2) this seam disappears — dependencies
 * arrive via layers (see docs/useful-blogs/effect-majesty-layers.md). */
export type ChunkEffectRunner<R> = {
  readonly runPromiseExit: <A, E>(
    effect: Effect.Effect<A, E, R>,
  ) => Promise<Exit.Exit<A, E>>;
};

/** The interruptibility-restore handed out by `uninterruptibleMask`, threaded
 * through planning so only the awaits are interruptible — never the
 * bookkeeping between the pending map and the work fiber. */
type ChunkFetchRestore = <A, E, R2>(
  effect: Effect.Effect<A, E, R2>,
) => Effect.Effect<A, E, R2>;

export type ChunkFetchRequest<T, E, R> = {
  key: string;
  effect: Effect.Effect<T, E, R>;
  /** Engine-owned storage (the chunk-cache atom, plus any offline fallback).
   * The fetcher only orchestrates. */
  readCached: () => T | undefined;
  writeCached: (value: T) => void;
};

type KeyState = {
  failures: number;
  nextEligibleAtMs: number;
  lastFailureAtMs: number;
  /** When this chunk first failed as the CURRENT chunk. A prefetch failure
   * backs the chunk off but never starts its retry budget: the budget is how
   * long a boundary waits on the chunk, and a prefetch can fail a whole chunk
   * before that boundary arrives. */
  budgetStartedAtMs: number | undefined;
  /** The server's `retry_after` floor, which binds even a chunk the listener
   * is waiting on. */
  retryAfterUntilMs: number;
  /** A 404/400 already seen, usually by the prefetch: the chunk's own answer
   * when it becomes current, so the boundary takes the broken-chunk skip. */
  permanentFailure: ChunkFetchFailedError | undefined;
};

/** One chunk's shared work. `parked` means the fiber is waiting on the
 * semaphore: it has touched nothing, so it is the only state a supersede or a
 * current-priority preemption may cancel. `superseded` hands ownership of the
 * outcome away from the work fiber to whoever cancelled it. */
type PendingFetch = {
  readonly key: string;
  readonly deferred: Deferred.Deferred<unknown, ChunkFetchError>;
  parked: boolean;
  superseded: boolean;
  fiber: Fiber.Fiber<unknown, ChunkFetchError> | undefined;
};

export function createChunkFetcher<R>({
  runner,
  nowMs = () => Date.now(),
  random = Math.random,
  config = defaultChunkFetcherConfig,
  onCircuitStateChange,
  onPrefetchDeferred,
}: {
  runner: ChunkEffectRunner<R>;
  nowMs?: () => number;
  random?: () => number;
  config?: ChunkFetcherConfig;
  onCircuitStateChange?: (state: ChunkFetchCircuitState) => void;
  /** A prefetch waiting for a free slot — pressure telemetry, not a skip. */
  onPrefetchDeferred?: (key: string, queueDepth: number) => void;
}) {
  const keyStates = new Map<string, KeyState>();
  const pending = new Map<string, PendingFetch>();
  let recentFailures: Array<{ key: string; atMs: number }> = [];
  let circuit: ChunkFetchCircuitState = 'closed';
  let lastProbeAtMs = Number.NEGATIVE_INFINITY;
  let probedSinceOpen = false;

  const slots = Semaphore.makeUnsafe(config.maxConcurrent);
  let slotsInUse = 0;
  // Work fibers belong to the fetcher, not to whichever caller happened to
  // start them: a caller that goes away must not abandon a fetch the others
  // are still waiting on.
  const scope = Scope.makeUnsafe();

  const ambientLog = (message: string) => {
    void runner.runPromiseExit(Effect.logWarning(message));
  };

  const setCircuit = (next: ChunkFetchCircuitState) => {
    if (circuit === next) return;
    circuit = next;
    if (next === 'open') probedSinceOpen = false;
    ambientLog(`audio chunk-fetch circuit ${next}`);
    onCircuitStateChange?.(next);
  };

  const pruneFailureWindow = (now: number) => {
    recentFailures = recentFailures.filter(
      (f) => now - f.atMs <= config.circuitWindowMs,
    );
  };

  const keyStateFor = (key: string, now: number): KeyState => {
    const existing = keyStates.get(key);
    if (existing) return existing;
    const fresh: KeyState = {
      failures: 0,
      nextEligibleAtMs: 0,
      lastFailureAtMs: now,
      budgetStartedAtMs: undefined,
      retryAfterUntilMs: 0,
      permanentFailure: undefined,
    };
    keyStates.set(key, fresh);
    return fresh;
  };

  const recordFailure = (
    key: string,
    classification: ApiRetryClass,
    retryAfterMs: number | undefined,
    priority: ChunkFetchPriority,
    failed?: ChunkFetchFailedError,
  ) => {
    const now = nowMs();
    const state = keyStateFor(key, now);
    // Quiet for a full budget window: a new incident, not the tail of the old
    // one — otherwise one fresh failure instantly exhausts the budget.
    if (
      state.failures > 0 &&
      state.nextEligibleAtMs !== Number.POSITIVE_INFINITY &&
      now - state.lastFailureAtMs >= config.retryBudgetMs
    ) {
      state.failures = 0;
      state.budgetStartedAtMs = undefined;
    }
    if (priority === 'current' && state.budgetStartedAtMs === undefined) {
      // The listener starts waiting on this chunk now. Prefetch failures
      // already ran its backoff up; carried over, the first current backoff
      // could outlast the whole budget and give up after one attempt.
      state.budgetStartedAtMs = now;
      state.failures = 0;
    }
    state.failures += 1;
    state.lastFailureAtMs = now;

    if (classification === 'permanent') {
      // Never retried: a 404/400 cannot succeed later. Not circuit evidence
      // either — one missing chunk says nothing about the backend.
      state.failures = config.maxAttemptsPerKey;
      state.nextEligibleAtMs = Number.POSITIVE_INFINITY;
      state.permanentFailure = failed;
      return;
    }
    if (classification === 'session') {
      // Auth-shaped failure: every subsequent request will fail the same way.
      setCircuit('open');
      return;
    }

    const backoffMs = Math.min(
      config.baseCooldownMs * 2 ** (state.failures - 1),
      config.maxCooldownMs,
    );
    // Full jitter (0.5x–1.5x) so a fleet of chunks doesn't retry in lockstep.
    const jitteredMs = backoffMs * (0.5 + random());
    state.retryAfterUntilMs = now + (retryAfterMs ?? 0);
    state.nextEligibleAtMs = now + Math.max(jitteredMs, retryAfterMs ?? 0);

    recentFailures.push({ key, atMs: now });
    pruneFailureWindow(now);
    const distinctKeys = new Set(recentFailures.map((f) => f.key));
    if (
      circuit === 'closed' &&
      distinctKeys.size >= config.circuitFailureThreshold
    ) {
      setCircuit('open');
    }
  };

  const recordSuccess = (key: string) => {
    keyStates.delete(key);
    recentFailures = recentFailures.filter((f) => f.key !== key);
    if (circuit === 'open') {
      // A successful probe closes the circuit; stale per-key cooldowns from
      // the outage would only delay recovery.
      keyStates.clear();
      recentFailures = [];
      setCircuit('closed');
    }
  };

  const admitOrSkip = (
    key: string,
    priority: ChunkFetchPriority,
  ): ChunkFetchError | null => {
    const now = nowMs();
    const state = keyStates.get(key);
    if (priority === 'current') {
      if (state?.permanentFailure) return state.permanentFailure;
      // The current chunk preempts the concurrency cap and the attempt cap
      // (it is governed by the time budget), but never the cooldown floor —
      // checked BEFORE the circuit so a probe can never re-hit a
      // server-directed `retry_after`. Until the chunk has failed as current,
      // only prefetch backed it off, and the listener waiting on it now is
      // owed an attempt: only `retry_after` holds it.
      const eligibleAtMs =
        state === undefined
          ? 0
          : state.budgetStartedAtMs === undefined &&
              state.nextEligibleAtMs !== Number.POSITIVE_INFINITY
            ? state.retryAfterUntilMs
            : state.nextEligibleAtMs;
      if (state && now < eligibleAtMs) {
        // A cooldown that outlives the retry budget can never produce another
        // attempt — surface gave-up so the engine faults instead of waiting.
        return eligibleAtMs >=
          (state.budgetStartedAtMs ?? now) + config.retryBudgetMs
          ? chunkFetchSkipped('gave-up', key)
          : chunkFetchSkipped('cooldown', key, eligibleAtMs - now);
      }
      if (circuit === 'open') {
        // The first current-priority fetch after an open probes immediately —
        // waiting out `probeIntervalMs` faulted the boundary chunk with zero
        // network attempts. Later probes pace as before.
        const probeEligible =
          !probedSinceOpen || now - lastProbeAtMs >= config.probeIntervalMs;
        if (!probeEligible) return chunkFetchSkipped('circuit-open', key);
        probedSinceOpen = true;
        // Pacing is measured from actual probes, not from the failures that
        // open or re-open the circuit.
        lastProbeAtMs = now;
      }
      return null;
    }
    if (circuit === 'open') return chunkFetchSkipped('circuit-open', key);
    if (state && state.failures >= config.maxAttemptsPerKey) {
      return chunkFetchSkipped('gave-up', key);
    }
    if (state && now < state.nextEligibleAtMs) {
      return chunkFetchSkipped('cooldown', key);
    }
    return null;
  };

  /** The network attempt plus its policy bookkeeping. Interruption cannot
   * reach here: only parked entries are ever cancelled, and `parked` flips to
   * false in the same synchronous step that admits the fetch to a slot. */
  const attempt = <T, E>(
    request: ChunkFetchRequest<T, E, R>,
    priority: ChunkFetchPriority,
  ): Effect.Effect<T, ChunkFetchFailedError, R> =>
    Effect.exit(Effect.timeout(request.effect, config.requestTimeoutMs)).pipe(
      Effect.flatMap((exit) => {
        if (Exit.isSuccess(exit)) {
          request.writeCached(exit.value);
          recordSuccess(request.key);
          return Effect.succeed(exit.value);
        }
        // The typed failure, not a squashed cause: `E` is the endpoint's
        // declared error union, which is what classification reads. A defect
        // has no `E` and classifies transient.
        const failure = exit.cause.reasons.find(Cause.isFailReason)?.error;
        const classification = apiRetryClass(failure);
        const retryAfterMs = apiRetryAfterMs(failure);
        const failed = chunkFetchFailed(
          classification,
          request.key,
          failure ?? Cause.squash(exit.cause),
          retryAfterMs,
        );
        recordFailure(
          request.key,
          classification,
          retryAfterMs,
          priority,
          failed,
        );
        return Effect.fail(failed);
      }),
    );

  /** Holds one slot for the duration of `body`, whether the slot came from the
   * semaphore's queue or was free for the taking. */
  const holdSlot = <A, E, R2>(
    entry: PendingFetch,
    body: Effect.Effect<A, E, R2>,
  ) =>
    Effect.acquireUseRelease(
      Effect.sync(() => {
        entry.parked = false;
        slotsInUse += 1;
      }),
      () => body,
      () =>
        Effect.sync(() => {
          slotsInUse -= 1;
        }),
    );

  const parkedCount = () => {
    let depth = 0;
    for (const entry of pending.values()) if (entry.parked) depth += 1;
    return depth;
  };

  /** Prefetch: park on the semaphore until a slot frees. Over-cap prefetches
   * wait instead of dropping — rejecting them starved the cold-start runway
   * (5 issued, 3 admitted, a guaranteed stall). */
  const runQueued = <T, E>(
    entry: PendingFetch,
    request: ChunkFetchRequest<T, E, R>,
  ): Effect.Effect<T, ChunkFetchError, R> =>
    Effect.suspend(() => {
      if (slotsInUse >= config.maxConcurrent) {
        entry.parked = true;
        onPrefetchDeferred?.(entry.key, parkedCount());
      }
      return slots.withPermits(1)(
        holdSlot(
          entry,
          // Policy may have moved while the fetch waited for its slot — a
          // grant must not bypass admission, and the chunk may have arrived by
          // another route in the meantime.
          Effect.suspend<T, ChunkFetchError, R>(() => {
            const cached = request.readCached();
            if (cached !== undefined) return Effect.succeed(cached);
            const skip = admitOrSkip(request.key, 'prefetch');
            return skip ? Effect.fail(skip) : attempt(request, 'prefetch');
          }),
        ),
      );
    });

  /** Current priority: never waits, but still consumes a slot when one is
   * free, so the cap stays the true bound on concurrent backend requests. */
  const runUncapped = <T, E>(
    entry: PendingFetch,
    request: ChunkFetchRequest<T, E, R>,
  ): Effect.Effect<T, ChunkFetchError, R> =>
    slots
      .withPermitsIfAvailable(1)(holdSlot(entry, attempt(request, 'current')))
      .pipe(
        Effect.flatMap((held) =>
          Option.isSome(held)
            ? Effect.succeed(held.value)
            : attempt(request, 'current'),
        ),
      );

  const settle = (
    entry: PendingFetch,
    exit: Exit.Exit<unknown, ChunkFetchError>,
  ) => {
    if (entry.superseded) return;
    if (pending.get(entry.key) === entry) pending.delete(entry.key);
    Deferred.doneUnsafe(entry.deferred, exit);
  };

  const work = <T, E>(
    request: ChunkFetchRequest<T, E, R>,
    entry: PendingFetch,
    priority: ChunkFetchPriority,
  ): Effect.Effect<T, ChunkFetchError, R> =>
    Effect.suspend<T, ChunkFetchError, R>(() => {
      if (entry.superseded) {
        return Effect.fail(chunkFetchSkipped('superseded', entry.key));
      }
      return priority === 'current'
        ? runUncapped(entry, request)
        : runQueued(entry, request);
    }).pipe(Effect.onExit((exit) => Effect.sync(() => settle(entry, exit))));

  /** The pending map is heterogeneous by key; the request that created the
   * entry owns its value type. */
  const awaitPending = <T>(entry: PendingFetch, restore: ChunkFetchRestore) =>
    restore(Deferred.await(entry.deferred)).pipe(
      Effect.map((value) => value as T),
    );

  const fork = <T, E>(
    request: ChunkFetchRequest<T, E, R>,
    entry: PendingFetch,
    priority: ChunkFetchPriority,
    restore: ChunkFetchRestore,
  ): Effect.Effect<T, ChunkFetchError, R> =>
    // The work must stay interruptible even though the fork site is masked:
    // preempt and reset cancel parked fibers.
    Effect.forkIn(Effect.interruptible(work(request, entry, priority)), scope, {
      startImmediately: true,
    }).pipe(
      Effect.flatMap((fiber) => {
        entry.fiber = fiber;
        return awaitPending<T>(entry, restore);
      }),
    );

  const launch = <T, E>(
    request: ChunkFetchRequest<T, E, R>,
    priority: ChunkFetchPriority,
    restore: ChunkFetchRestore,
  ) => {
    const entry: PendingFetch = {
      key: request.key,
      deferred: Deferred.makeUnsafe<unknown, ChunkFetchError>(),
      parked: false,
      superseded: false,
      fiber: undefined,
    };
    pending.set(request.key, entry);
    return fork(request, entry, priority, restore);
  };

  /** The current chunk preempts its own parked prefetch: run it now, uncapped,
   * and settle the prefetch's waiters with the same outcome. */
  const preempt = <T, E>(
    request: ChunkFetchRequest<T, E, R>,
    parkedEntry: PendingFetch,
    restore: ChunkFetchRestore,
  ) => {
    parkedEntry.superseded = true;
    pending.delete(parkedEntry.key);
    parkedEntry.fiber?.interruptUnsafe();

    const skip = admitOrSkip(request.key, 'current');
    const promoted = skip
      ? Effect.fail<ChunkFetchError>(skip)
      : launch(request, 'current', restore);
    return promoted.pipe(
      Effect.onExit((exit) =>
        Effect.sync(() => Deferred.doneUnsafe(parkedEntry.deferred, exit)),
      ),
    );
  };

  const plan = <T, E>(
    request: ChunkFetchRequest<T, E, R>,
    priority: ChunkFetchPriority,
    restore: ChunkFetchRestore,
  ): Effect.Effect<T, ChunkFetchError, R> => {
    const cached = request.readCached();
    if (cached !== undefined) return Effect.succeed(cached);

    const existing = pending.get(request.key);
    if (existing) {
      // A prefetch always joins the shared work; so does a current fetch once
      // that work holds a slot. Only a still-parked prefetch gets preempted.
      return priority === 'prefetch' || !existing.parked
        ? awaitPending<T>(existing, restore)
        : preempt(request, existing, restore);
    }

    const skip = admitOrSkip(request.key, priority);
    if (skip) return Effect.fail(skip);
    return launch(request, priority, restore);
  };

  /** Parked prefetches belong to a superseded runway; fetches that already
   * hold a slot are left alone — their results are still cacheable and
   * cancelling them would surface as an untyped rejection. */
  const cancelParked = () => {
    for (const entry of pending.values()) {
      if (!entry.parked) continue;
      entry.superseded = true;
      pending.delete(entry.key);
      Deferred.doneUnsafe(
        entry.deferred,
        Exit.fail(chunkFetchSkipped('superseded', entry.key)),
      );
      entry.fiber?.interruptUnsafe();
    }
  };

  return {
    fetchEffect: <T, E>(
      request: ChunkFetchRequest<T, E, R>,
      priority: ChunkFetchPriority,
    ): Effect.Effect<T, ChunkFetchError, R> =>
      Effect.uninterruptibleMask((restore) =>
        Effect.suspend(() => plan(request, priority, restore)),
      ),
    circuitState: () => circuit,
    /** True once the current chunk has been failing longer than the retry
     * budget — the engine's cue to stop retrying and surface a fault. */
    retryBudgetExhausted: (key: string) => {
      const state = keyStates.get(key);
      if (state?.budgetStartedAtMs === undefined) return false;
      return nowMs() - state.budgetStartedAtMs >= config.retryBudgetMs;
    },
    /** Non-fetch playback failures (load timeout, decoder errors) count as
     * transient evidence against the same chunk so the budget and circuit see
     * them too. */
    notePlaybackFailure: (key: string) => {
      recordFailure(key, 'transient', undefined, 'current');
    },
    /** A chunk actually became playable — clears its failure state (and, after
     * a successful probe, closes the circuit). */
    notePlaybackSuccess: (key: string) => {
      recordSuccess(key);
    },
    failureCount: (key: string) => keyStates.get(key)?.failures ?? 0,
    /** An explicit user retry (pressing play on a faulted player) should probe
     * immediately instead of waiting out the probe interval. */
    noteUserRetry: () => {
      lastProbeAtMs = Number.NEGATIVE_INFINITY;
      // A fresh user intent lifts per-key cooldowns and restarts the retry
      // budget; recentFailures stays — circuit evidence is about the backend.
      keyStates.clear();
    },
    cancelParked,
    reset: () => {
      keyStates.clear();
      recentFailures = [];
      lastProbeAtMs = Number.NEGATIVE_INFINITY;
      probedSinceOpen = false;
      cancelParked();
      setCircuit('closed');
    },
  };
}
